import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getMe,
  login as loginRequest,
  logout as logoutRequest,
} from '../api/auth';
import {
  SESSION_EXPIRED_EVENT,
  SESSION_REFRESHED_EVENT,
} from '../api/client';
import type {
  AuthenticationResult,
  AuthBusiness,
  AuthStore,
  AuthUser,
  LoginInput,
} from '../types/auth';

interface AuthContextValue {
  business: AuthBusiness | null;
  user: AuthUser | null;
  stores: AuthStore[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [business, setBusiness] = useState<AuthBusiness | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [stores, setStores] = useState<AuthStore[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const initializedRef = useRef(false);

  const clearSession = useCallback(() => {
    setBusiness(null);
    setUser(null);
    setStores([]);
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => clearSession();
    const handleSessionRefreshed = (event: Event) => {
      const result = (event as CustomEvent<AuthenticationResult>).detail;
      setBusiness(result.business);
      setUser(result.user);
      setStores(result.stores);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener(SESSION_REFRESHED_EVENT, handleSessionRefreshed);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener(SESSION_REFRESHED_EVENT, handleSessionRefreshed);
    };
  }, [clearSession]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const loadSession = async () => {
      localStorage.removeItem('conolandia.accessToken');
      localStorage.removeItem('conolandia.refreshToken');

      try {
        const profile = await getMe();
        setBusiness(profile.business);
        setUser(profile.user);
        setStores(profile.stores);
      } catch {
        clearSession();
      } finally {
        setIsLoading(false);
      }
    };

    void loadSession();
  }, [clearSession]);

  const login = useCallback(
    async (input: LoginInput) => {
      const result = await loginRequest(input);
      setBusiness(result.business);
      setUser(result.user);
      setStores(result.stores);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      business,
      user,
      stores,
      isAuthenticated: user !== null,
      isLoading,
      login,
      logout,
    }),
    [business, isLoading, login, logout, stores, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}
